import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef, useState } from 'react';
import { Send, Check, AlertCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';

// Google Form Configuration
const GOOGLE_FORM_BASE_URL = "https://docs.google.com/forms/d/e/1FAIpQLSeRCgwi0gvNTWrzcAFqDSbCDA3H2OxkbGIygh7nrUj55Kk49Q/formResponse";

const RSVPSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    companion: '',
    guests: '1',
    allergies: '',
    comments: ''
  });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08,
        delayChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 25 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: 'easeOut' },
    },
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (value) => {
    setFormData(prev => ({ ...prev, guests: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Build URL with parameters
    const params = new URLSearchParams({
      'entry.52433357': formData.name,
      'entry.2075016692': formData.companion,
      'entry.18636594': formData.guests,
      'entry.1788688507': formData.allergies,
      'entry.1204714434': formData.comments,
      'submit': 'Submit'
    });

    try {
      // Use fetch with no-cors mode
      await fetch(`${GOOGLE_FORM_BASE_URL}?${params.toString()}`, {
        method: 'GET',
        mode: 'no-cors'
      });
    } catch (error) {
      // Ignore errors - no-cors doesn't allow us to read the response anyway
      console.log('Form submission attempted');
    }

    // Show success after a brief delay
    setTimeout(() => {
      setIsSubmitting(false);
      setIsSubmitted(true);
    }, 1000);
  };

  return (
    <section 
      ref={ref}
      className="py-16 md:py-20 px-6 bg-gradient-to-b from-[#F5F0EB] via-[#F5F0EB] to-[#F9F7F2]"
      data-testid="rsvp-section"
    >
      <motion.div
        className="max-w-2xl mx-auto"
        variants={containerVariants}
        initial="hidden"
        animate={isInView ? "visible" : "hidden"}
      >
        {/* Section Title */}
        <motion.div className="text-center mb-10 md:mb-12" variants={itemVariants}>
          <span className="font-accent md:text-4xl !text-5xl text-[#CCA598]">Confirma tu asistencia</span>
          <h2 className="font-heading md:text-3xl !text-2xl mt-1 text-[#5D4037]">
          </h2>
          <p className="font-body max-w-md !text-sm mt-3 mx-auto text-[#8D6E63]">
            Por favor, confirma tu asistencia antes del 12 de junio de 2026
          </p>
        </motion.div>

        {/* Note about guests */}
        <motion.div 
          className="flex items-start gap-3 p-4 bg-[#CCA598]/10 border-l-2 border-[#CCA598] mb-6"
          variants={itemVariants}
        >
          <AlertCircle className="w-5 h-5 text-[#8D6E63] flex-shrink-0 mt-0.5" />
          <p className="font-body text-sm text-[#5D4037]">
            Cada invitación es válida para un máximo de 2 personas. Si deseas traer un acompañante, por favor indica su nombre.
          </p>
        </motion.div>

        {!isSubmitted ? (
          <motion.form 
            onSubmit={handleSubmit}
            className="space-y-6"
            variants={containerVariants}
            data-testid="rsvp-form"
          >
            {/* Name Field */}
            <motion.div variants={itemVariants} className="space-y-2">
              <Label 
                htmlFor="name" 
                className="font-body text-xs tracking-[0.15em] uppercase text-[#8D6E63]"
              >
                Tu nombre *
              </Label>
              <Input
                id="name"
                name="name"
                type="text"
                required
                value={formData.name}
                onChange={handleChange}
                className="bg-transparent border-0 border-b-2 border-[#D7CCC8] rounded-none focus:border-[#CCA598] focus:ring-0 text-[#3E2723] placeholder:text-[#8D6E63]/50 py-3"
                placeholder="Nombre completo"
                data-testid="rsvp-name-input"
              />
            </motion.div>

            {/* Companion Field */}
            <motion.div variants={itemVariants} className="space-y-2">
              <Label 
                htmlFor="companion" 
                className="font-body text-xs tracking-[0.15em] uppercase text-[#8D6E63]"
              >
                Nombre del acompañante (opcional)
              </Label>
              <Input
                id="companion"
                name="companion"
                type="text"
                value={formData.companion}
                onChange={handleChange}
                className="bg-transparent border-0 border-b-2 border-[#D7CCC8] rounded-none focus:border-[#CCA598] focus:ring-0 text-[#3E2723] placeholder:text-[#8D6E63]/50 py-3"
                placeholder="Nombre de tu acompañante"
                data-testid="rsvp-companion-input"
              />
            </motion.div>

            {/* Number of Guests */}
            <motion.div variants={itemVariants} className="space-y-2">
              <Label 
                htmlFor="guests" 
                className="font-body text-xs tracking-[0.15em] uppercase text-[#8D6E63]"
              >
                Número de personas *
              </Label>
              <Select value={formData.guests} onValueChange={handleSelectChange}>
                <SelectTrigger 
                  className="bg-transparent border-0 border-b-2 border-[#D7CCC8] rounded-none focus:ring-0 text-[#3E2723]"
                  data-testid="rsvp-guests-select"
                >
                  <SelectValue placeholder="Selecciona" />
                </SelectTrigger>
                <SelectContent className="bg-[#F9F7F2] border-[#D7CCC8]">
                  <SelectItem value="1" className="text-[#3E2723] focus:bg-[#F5F0EB]">1 persona</SelectItem>
                  <SelectItem value="2" className="text-[#3E2723] focus:bg-[#F5F0EB]">2 personas</SelectItem>
                </SelectContent>
              </Select>
            </motion.div>

            {/* Allergies Field */}
            <motion.div variants={itemVariants} className="space-y-2">
              <Label 
                htmlFor="allergies" 
                className="font-body text-xs tracking-[0.15em] uppercase text-[#8D6E63]"
              >
                Alergias o intolerancias alimentarias
              </Label>
              <Input
                id="allergies"
                name="allergies"
                type="text"
                value={formData.allergies}
                onChange={handleChange}
                className="bg-transparent border-0 border-b-2 border-[#D7CCC8] rounded-none focus:border-[#CCA598] focus:ring-0 text-[#3E2723] placeholder:text-[#8D6E63]/50 py-3"
                placeholder="Ej: Sin gluten, vegetariano..."
                data-testid="rsvp-allergies-input"
              />
            </motion.div>

            {/* Comments Field */}
            <motion.div variants={itemVariants} className="space-y-2">
              <Label 
                htmlFor="comments" 
                className="font-body text-xs tracking-[0.15em] uppercase text-[#8D6E63]"
              >
                Comentarios adicionales
              </Label>
              <Textarea
                id="comments"
                name="comments"
                value={formData.comments}
                onChange={handleChange}
                className="bg-[#F9F7F2]/50 border-2 border-[#D7CCC8] rounded-none focus:border-[#CCA598] focus:ring-0 text-[#3E2723] placeholder:text-[#8D6E63]/50 min-h-[80px] resize-none"
                placeholder="¿Algo que debamos saber?"
                data-testid="rsvp-comments-textarea"
              />
            </motion.div>

            {/* Submit Button */}
            <motion.div variants={itemVariants} className="pt-2">
              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full font-body text-sm tracking-wider uppercase bg-[#5D4037] text-[#F9F7F2] hover:bg-[#4A3228] transition-all duration-300 py-4 rounded-none shadow-md"
                data-testid="rsvp-submit-btn"
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Enviando...
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <Send className="w-4 h-4" />
                    Confirmar asistencia
                  </span>
                )}
              </Button>
            </motion.div>
          </motion.form>
        ) : (
          <motion.div 
            className="text-center py-10 bg-[#8F9779]/10 rounded-sm"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            data-testid="rsvp-success"
          >
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#8F9779]/20 mb-4">
              <Check className="w-7 h-7 text-[#8F9779]" />
            </div>
            <h3 className="font-heading text-xl text-[#5D4037] mb-2">¡Gracias por confirmar!</h3>
            <p className="font-body text-sm text-[#8D6E63]">
              Hemos recibido tu confirmación. ¡Nos vemos el 2 de octubre!
            </p>
          </motion.div>
        )}
      </motion.div>
    </section>
  );
};

export default RSVPSection;
